import '../styles/globals.scss';

// `fetch` is not in the polyfill bundle because it's the only thing missing on iOS 9
// and we don't want to force a lot of unneeded polyfills just for that
import 'unfetch/polyfill';
import React from 'react';
import { Provider as StoreProvider } from 'react-redux';
import { GlitzClient } from '@glitz/core';
import { GlitzProvider } from '@glitz/react';
import 'Shared/component-registry';
import 'intersection-observer';
import { initApplicationInsights, trackPageview } from './telemetry';
import {
  browserRender,
  initUpdateAppShellDataOnSchedule,
  loadPage,
  switchBreakpoint,
  currentBreakpoint,
  setupResizeListener,
  resolveComponentAndChildComponents,
  isBundleLoadError,
  setPhrases,
  scrollToHashAfterInitialRender,
  addUserLog,
  userLogToString,
  setStoreForCrossWindowEvents,
  onHistory,
  refreshCachedData,
  hasServiceWorker,
  clearCacheAndUnregisterServiceWorker,
  registerServiceWorker,
  getAppShellData,
  saveAppShellData,
} from '@avensia/scope';
import SiteLayout from './SiteLayout';
import State, { PageType, Store, CartType } from 'Shared/State';
import AppShellDataType from 'AppShell/AppShellData.type';
import createStore from 'Shared/create-store';
import addToCartFromUrl from './Cart/add-to-cart-from-url';
import { setKnownUrls } from 'Shared/known-urls';
import { initTagManager, pageLoad as gtmPageLoad } from './TrackingInformation';
import currentPageIsAppShell from './AppShell/Partials/current-page-is-appshell';
import { glitzCoreOptions } from 'Shared/glitz-options';
import ThemeProvider from 'Shared/Theming';
import { initKlaviyo } from 'TrackingInformation/Klaviyo';

let firstLoadIsAppShell = false;

async function render(store: Store, glitz: GlitzClient) {
  const el = document.getElementById('container');

  if (!(window as any).Intl) {
    // Price formatting uses `Intl.NumberFormat` which isn't supported for
    // browsers like IE10 and iOS9
    await import('Shared/intl');
  }

  await browserRender(
    store,
    el,
    <StoreProvider store={store}>
      <GlitzProvider glitz={glitz}>
        <ThemeProvider>
          <SiteLayout />
        </ThemeProvider>
      </GlitzProvider>
    </StoreProvider>,
  );

  if (location.hash) {
    await scrollToHashAfterInitialRender(location.hash);
  }
}

function initialRender(appShellData: AppShellDataType, currentPage: PageType, glitz: GlitzClient) {
  let initialLog = 'Started at ' + window.location.href + '.';
  if (window.IS_RENDERED_FROM_CACHE) {
    initialLog += ' Was rendered in Service Worker.';
  }
  addUserLog(initialLog);

  setKnownUrls(appShellData.siteSettings);
  const store = createStore({ appShellData, currentPage });
  initApplicationInsights(appShellData);
  // Since some errors occur before this code has loaded some errors slip past this check.
  // Because of this a slightly different version of this is placed in _Layout.cshtml
  // as well
  if (window.rg4js) {
    window.rg4js('onBeforeSend', (payload: RaygunPayload) => {
      function stackTraceHasNoInformation(stackTrace: RaygunStackItem[]) {
        if (!stackTrace || stackTrace.length === 0) {
          return true;
        }
        const firstItem = stackTrace[0];
        const lastItem = stackTrace[stackTrace.length - 1];
        return (
          (!firstItem.ColumnNumber && !firstItem.LineNumber) ||
          (!firstItem.ColumnNumber && firstItem.LineNumber === 1) ||
          // If the only stack item is from window.onerror we want to ignore it
          (stackTrace.length === 1 &&
            (!firstItem.MethodName || firstItem.MethodName.toLowerCase().includes('onerror'))) ||
          // Any errors from global code is not our fault
          (lastItem.MethodName || '').toLowerCase().includes('global code') ||
          // A bug in Chrome for iOS sometimes propagates errors to us for autofill
          (firstItem.MethodName || '').includes('getUnownedAutofillableFormFieldElements_') ||
          firstItem.MethodName === '?' ||
          (firstItem.FileName || '').includes('googleusercontent.com')
        );
      }

      const ignores = ['InvalidStateError', 'Network request failed', '__gCrWeb'];

      for (const ignore of ignores) {
        if (payload.Details.Error.Message.indexOf(ignore) !== -1) {
          return null;
        }
      }
      if (stackTraceHasNoInformation(payload.Details.Error.StackTrace)) {
        return null;
      }
      if (!payload.Details.UserCustomData) {
        payload.Details.UserCustomData = {};
      }
      payload.Details.UserCustomData.userLog = userLogToString();
      payload.Details.UserCustomData.currentState = store.getState();
      return payload;
    });

    window.rg4js('groupingKey', (payload: RaygunPayload) => {
      return payload.Details.Error.Message;
    });
  }

  initKlaviyo(store);
  initTagManager(store);
  setStoreForCrossWindowEvents(store);
  setupResizeListener(store);
  addToCartFromUrl(store);
  initUpdateAppShellDataOnSchedule(store);

  const breakpoint = currentBreakpoint();
  if (breakpoint !== store.getState().currentBreakpoint) {
    console.debug('Setting new breakpoint since server guess was incorrect');
    store.dispatch(switchBreakpoint(breakpoint));
  } else {
    console.debug('Server breakpoint guess was correct');
  }

  onHistory(['push', 'pop', 'replace'], (e) => {
    const loadPromise = store.dispatch(loadPage({ url: e.url, options: e.options, stateChangeEvent: e }));
    loadPromise.then(() => {
      const state = store.getState() as State;
      const currentPage = state.currentPage;
      if (!currentPage.isPartial) {
        setTimeout(() => {
          gtmPageLoad(currentPage, true, state);
          trackPageview(page);
        }, 1000);
      }
    });
    return loadPromise;
  });

  // Make sure phrases isn't `undefined`, otherwise
  // `translate(...)` with cause an exception.
  setPhrases(appShellData.languagePhrases);

  const page = (store.getState() as State).currentPage;
  if (!currentPageIsAppShell(page)) {
    gtmPageLoad(page, false, store.getState() as State);
  } else {
    firstLoadIsAppShell = true;
  }

  render(store, glitz);

  return store;
}
window.scopeReady.then(() => {
  const appShellData: AppShellDataType = (window as any).APP_SHELL_DATA;
  const currentPage = (window as any).CURRENT_PAGE;
  if (!appShellData && !currentPage) {
    if (window.rg4js) {
      window.rg4js('send', {
        error: new Error(
          'Script error happened on ' + window.location.href + ' that caused important globals not to exist',
        ),
        tags: ['handled'],
      });
    }
    if (window.location.href !== '/') {
      // We assume that the error doesn't occur on the start page
      // Give Raygun some time to send the request
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  } else {
    if (module.hot && module.hot.data && module.hot.data.store) {
      const store = module.hot.data.store;
      const glitz = module.hot.data.glitz;

      module.hot.accept();
      module.hot.dispose((data) => {
        data.store = store;
        data.glitz = glitz;
      });

      const state = store.getState();

      console.info('HMR updating');
      resolveComponentAndChildComponents([state.currentPage, state.appShellData], state.appShellData.currentTheme).then(
        () => {
          console.info('HMR re-render');
          render(store, glitz);
        },
        (e) => {
          console.error('HMR reloading due to update error: ', e);
          window.location.reload();
        },
      );
    } else {
      resolveComponentAndChildComponents(
        [window.CURRENT_PAGE, window.APP_SHELL_DATA],
        (window.APP_SHELL_DATA as AppShellDataType).currentTheme,
      ).then(
        () => {
          const glitz = new GlitzClient(glitzCoreOptions);
          const store = initialRender(window.APP_SHELL_DATA, window.CURRENT_PAGE, glitz);

          if (module.hot) {
            module.hot.accept();
            module.hot.dispose((data) => {
              data.store = store;
              data.glitz = glitz;
            });
          }

          if (window.IS_RENDERED_FROM_CACHE) {
            persistCartAndWishListChanges(store);
            refreshCachedData(store).then(
              () => {
                const page = (store.getState() as State).currentPage;
                if (firstLoadIsAppShell) {
                  gtmPageLoad(page, false, store.getState() as State);
                  firstLoadIsAppShell = false;
                }
              },
              () => {
                const page = (store.getState() as State).currentPage;
                gtmPageLoad(page, false, store.getState() as State);
              },
            );
          }
        },
        (e) => {
          if (isBundleLoadError(e) && hasServiceWorker) {
            clearCacheAndUnregisterServiceWorker().then(() => {
              window.location.reload();
            });
          }
        },
      );
    }

    registerServiceWorker();

    const oldOnError = window.onerror;
    if (process.env.NODE_ENV !== 'production') {
      // Make device debugging a bit simpler
      // tslint:disable-next-line only-arrow-functions
      window.onerror = function (message, url, line, col, e) {
        const error = document.createElement('div');
        error.style.position = 'fixed';
        error.style.padding = '10px';
        error.style.border = '1px solid red';
        error.style.top = '10px';
        error.style.left = '10px';
        error.style.right = '10px';
        error.style.bottom = '10px';
        error.style.background = 'white';

        const html =
          '<h1>Oh noes!</h1>' +
          '<p>Message: ' +
          message +
          '</p>' +
          '<p>Line: ' +
          line +
          '</p>' +
          '<p>Error: ' +
          JSON.stringify(e, null, 2) +
          '</p>' +
          '<p>' +
          url +
          '</p>';
        error.innerHTML = html;
        document.body.appendChild(error);
        error.addEventListener('click', () => document.body.removeChild(error));

        if (oldOnError) {
          oldOnError.apply(window, arguments);
        }
      };
    } else {
      printAvensiaSplashToConsole();
    }
  }
});
// The cart is one of the things in the app shell data that the user can
// change and we want to persist those changes. Because if the user reloads
// without network connection to refresh the app shell data, the cart should
// still be correct.
function persistCartAndWishListChanges(store: Store) {
  let currentCart = store.getState().cart;
  store.subscribe(() => {
    const newCart = store.getState().cart;
    if (currentCart !== newCart) {
      saveAppShellDataWithNewCart(newCart);
      currentCart = newCart;
    }
  });
}

let saveTimer: number;
function saveAppShellDataWithNewCart(cart: CartType) {
  clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    getAppShellData().then((data) => {
      const appShellData = data as AppShellDataType;
      console.info('Persisting change to app shell data');
      appShellData.cart = cart;
      saveAppShellData(appShellData);
    });
  }, 200);
}

function printAvensiaSplashToConsole() {
  if (window.console && window.console.log) {
    window.console.log(
      '     _                      _       \n' +
        '    / \\__   _____ _ __  ___(_) __ _ \n' +
        "   / _ \\ \\ / / _ | '_ \\/ __| |/ _` |\n" +
        '  / ___ \\ V |  __| | | \\__ | | (_| |\n' +
        ' /_/   \\_\\_/ \\___|_| |_|___|_|\\__,_|\n\n' +
        'Do you end up here a lot? Perhaps you should come work for us!\n' +
        'We build sites using React, TypeScript, Webpack, CSS Modules, Episerver, etc.\n' +
        'http://www.avensia.com/careers/',
    );
  }
}

type RaygunStackItem = {
  LineNumber: number;
  ColumnNumber: number;
  ClassName: string;
  FileName: string;
  MethodName: string;
};

type RaygunPayload = {
  OccurredOn: Date;
  Details: {
    Error: {
      ClassName: string;
      Message: string;
      StackTrace: RaygunStackItem[];
    };
    Tags: string[];
    UserCustomData: any;
  };
};
